import { useAuth0 } from "@auth0/auth0-react";
import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import * as Sentry from "@sentry/react";
import { subDays } from "date-fns";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { validateAuth0ApiErrors } from "../../context/utilsAuth0";
import { downloadFileApiCall } from "../../networking";
import { ActionMenuButton } from "../ActionsMenu/ActionMenuButton";
import { LoadingModal } from "../LoadingModal/LoadingModal";

interface DownloadSamplesButtonProps {
  flow_source_id: number | string;
  initialMode?: string;
  finishedDate?: string | null;
  onClick: () => void;
  downloadStatus: string;
}

const DownloadSamplesButton: FC<DownloadSamplesButtonProps> = ({
  flow_source_id,
  onClick,
  finishedDate,
  downloadStatus,
}) => {
  const navigate = useNavigate();

  const [controller, setController] = useState<AbortController | null>(null);
  const [showLoadingModal, setShowLoadingModal] = useState(false);

  const handleCancel = () => {
    controller?.abort();
    setShowLoadingModal(false);
  };

  const { getAccessTokenSilently } = useAuth0();

  const getAccessToken = async () => {
    try {
      return await getAccessTokenSilently();
    } catch {
      // Token errors are handled by the overlay controller
      return null;
    }
  };

  const downloadFile = async () => {
    let link: HTMLAnchorElement | null = null;
    const accessToken = await getAccessToken();
    if (!accessToken) return;

    const newController = new AbortController();
    setController(newController);
    onClick();
    setShowLoadingModal(true);

    downloadFileApiCall(flow_source_id, accessToken, newController.signal)
      .then((response) => {
        validateAuth0ApiErrors(response, navigate);
        // Success
        const url = window.URL.createObjectURL(new Blob([response.data]));
        link = document.createElement("a");
        link.href = url;
        const filename =
          response.headers["content-disposition"]?.split("filename=")[1] || new Date().toISOString() + "_samples.txt";
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
      })
      .catch(function (error) {
        if (error.code === "ERR_CANCELED") {
          // The error was cancelled by the abort controller
          return;
        }
        Sentry.captureException(error);

        // Error
        const reader = new FileReader();
        reader.onload = function () {
          toast.error("There was an error with the download");
        };
        reader.readAsText(new Blob([error.response.data], { type: "application/json" }));
      })
      .then(function () {
        // Always Runs
        if (link) {
          link.parentNode?.removeChild(link);
        }
        setShowLoadingModal(false);
      });
  };

  const expiredDate = subDays(new Date(), 30);
  const isExpired = finishedDate && expiredDate > new Date(finishedDate);
  const isNotCompleted = downloadStatus !== "completed";
  const disabled = isExpired || isNotCompleted || false;

  return (
    <>
      <ActionMenuButton
        Icon={DownloadSimpleIcon}
        onClick={downloadFile}
        text="Download Samples"
        tooltipText={
          isNotCompleted ? "Only enabled when the Download is completed." : "Samples are expired after 30 days."
        }
        disabled={disabled}
      />

      <LoadingModal handleCancel={handleCancel} showLoadingModal={showLoadingModal} text="Downloading information" />
    </>
  );
};

export { DownloadSamplesButton };
